import React, { useState, useEffect } from 'react';
import {
    UserCheck,
    Award,
    BookOpen,
    Star,
    Mail,
    Phone,
    MoreVertical,
    Edit,
    Trash2
} from 'lucide-react';

const Trainer = ({ searchKey, idCustomer }) => {
    const [trainers, setTrainers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dropdownOpen, setDropdownOpen] = useState(null);

    // Fonction pour récupérer les formateurs
    const fetchTrainers = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await fetch('https://search.mg.formafusion.io/api/search/trainers', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({
                    key: searchKey,
                    idCustomer: idCustomer,
                }),
            });

            if (!response.ok) {
                throw new Error(`Erreur HTTP: ${response.status}`);
            }

            const data = await response.json();
            setTrainers(data.trainers);
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (searchKey && idCustomer) {
            fetchTrainers();
        }
    }, [searchKey, idCustomer]);

    // Handlers pour les actions
    const editTrainer = (id) => {
        setDropdownOpen(null);
    };

    const deleteTrainer = async (id) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer ce formateur ?')) {
            try {
                await fetchTrainers();
            } catch (error) {
                console.error('Erreur lors de la suppression:', error);
            }
        }
        setDropdownOpen(null);
    };

    const toggleDropdown = (id) => {
        setDropdownOpen(dropdownOpen === id ? null : id);
    };

    // Fermer le dropdown quand on clique ailleurs
    useEffect(() => {
        const handleClickOutside = () => {
            setDropdownOpen(null);
        };

        if (dropdownOpen) {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [dropdownOpen]);

    const renderStars = (rating) => {
        const stars = [];
        const fullStars = Math.floor(rating || 0);
        const hasHalfStar = (rating || 0) % 1 !== 0;

        for (let i = 0; i < fullStars; i++) {
            stars.push(<Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />);
        }

        if (hasHalfStar) {
            stars.push(<Star key="half" className="w-4 h-4 text-yellow-400 fill-yellow-400/50" />);
        }

        const emptyStars = 5 - Math.ceil(rating || 0);
        for (let i = 0; i < emptyStars; i++) {
            stars.push(<Star key={`empty-${i}`} className="w-4 h-4 text-gray-300" />);
        }

        return stars;
    };

    // Composant de chargement
    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-12 h-12 border-b-2 border-green-600 rounded-full animate-spin"></div>
            </div>
        );
    }

    // Composant d'erreur
    if (error) {
        return (
            <div className="p-4 text-center text-red-500">
                <p>Erreur: {error}</p>
                <button
                    onClick={fetchTrainers}
                    className="px-4 py-2 mt-2 text-white bg-green-600 rounded hover:bg-green-700"
                >
                    Réessayer
                </button>
            </div>
        );
    }

    // Aucun formateur trouvé
    if (!trainers || trainers.length === 0) {
        return (
            <div className="p-8 text-center text-gray-500">
                <UserCheck className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p>Aucun formateur trouvé pour "{searchKey}".</p>
            </div>
        );
    }

    return (
        <div className="container p-6 mx-auto">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-semibold text-gray-800">
                    Vous avez {trainers.length} Formateur{trainers.length > 1 ? 's' : ''}
                </h1>
                <button
                    onClick={fetchTrainers}
                    className="px-4 py-2 text-white transition-colors bg-green-600 rounded-lg hover:bg-green-700"
                >
                    Actualiser
                </button>
            </div>

            <section className="p-2 mt-2">
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 md:grid-cols-2">
                    {trainers.map((trainer) => (
                        <div class="grid w-full col-span-1 p-4 bg-white shadow-xl h-72 rounded-xl">
                            <div class="flex flex-col w-full h-full gap-2">
                                <div class="flex h-1/2 space-x-4">
                                    <div class="flex items-center justify-center w-24 h-24 text-3xl font-medium text-gray-500 uppercase bg-gray-100 rounded-full">
                                        <img src={`https://formafusionmg.ams3.cdn.digitaloceanspaces.com/formafusionmg/img/formateurs/${trainer.photo}`} alt="photo" class="object-cover w-full h-full rounded-full" />
                                    </div>
                                    <div class="flex flex-col justify-center items-center">
                                        <p class="flex-wrap text-lg text-slate-600">{trainer.name}</p>
                                        <p class="flex-wrap text-lg text-slate-600">{trainer.firstName}</p>
                                    </div>
                                </div>

                                <div class="w-full bg-slate-50 h-1/2 rounded-xl">
                                    <div class="flex flex-col h-full gap-2 p-3">
                                        <div class="w-full">
                                            <div class="grid grid-cols-5">

                                                <div class="grid justify-start col-span-2 grid-cols-subgrid">
                                                    <div class="flex flex-col">

                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="flex flex-col w-full gap-1">
                                            <div class="inline-flex items-center space-x-2 gap-1">
                                                <div class="w-[18px]">
                                                    <Mail className='text=gray-600'></Mail>
                                                </div>
                                                <p class="text-gray-500">{trainer.email}</p>
                                            </div>
                                            <div class="inline-flex items-center space-x-2 gap-1">
                                                <div class="w-[18px]">
                                                    <Phone className='w-6 h-6 text-gray-600'></Phone>
                                                </div>
                                                <p class="text-gray-500">{trainer.phone}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
};

export default Trainer;